<script setup>
import { ref, computed, watch } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import {
    PhNotePencil, PhPencilSimple, PhTrash, PhCaretRight, PhBook, PhBank,
} from '@phosphor-icons/vue'

import Layout from '@pages/Shared/layouts/StaffLayout.vue'
import Prompt from '@pages/Shared/Prompt.vue'
import Table  from '@pages/Shared/ui/Table.vue'

import AssessmentModal from '@pages/staff/components/AssessmentModal.vue'

import { flashToast } from '@/toast.js'

const props = defineProps({
    assessments:  Array,
    sessions:     Array,
    terms:        Array,
    classes:      Array,
    sessionTerms: Array,
    types:        Array,
    filters:      Object,
})

const columns = [
    { key: 'index',   label: '#',          type: 'index' },
    { key: 'name',    label: 'Assessment', type: 'slot'  },
    { key: 'session', label: 'Session',    type: 'slot'  },
    { key: 'term',    label: 'Term',       type: 'slot'  },
    { key: 'type',    label: 'Type',       type: 'slot'  },
    { key: 'weight',  label: 'Weight',     type: 'slot'  },
    { key: 'classes', label: 'Classes',    type: 'slot'  },
    { key: 'status',  label: 'Status',     type: 'slot'  },
    { key: 'actions', label: '',           type: 'slot'  },
]

const filters = ref({
    search:  props.filters?.search  ?? null,
    session: props.filters?.session ?? null,
    term:    props.filters?.term    ?? null,
    type:    props.filters?.type    ?? null,
})

watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true })

const termFilterOptions = computed(() => {
    const unique = [...new Map(props.terms.map(t => [t.name, t])).values()]
    return [
        { label: 'All Terms', value: null },
        ...unique.map(t => ({ label: t.name, value: t.name })),
    ]
})

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value)
            .filter(([_, v]) => v !== null && v !== undefined && v !== '')
    )
    router.get('/staff/manage-assessments', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch = (value)      => { filters.value.search = value; applyFilters() }
const handleFilter = (newFilters) => { filters.value = { ...filters.value, ...newFilters }; applyFilters() }

const expandedId   = ref(null)
const toggleExpand = (id) => { expandedId.value = expandedId.value === id ? null : id }

const getTotalSubjects = (classes) =>
    classes.reduce((sum, cls) => sum + (cls.subjects?.length || 0), 0)

// ─── Modal ────────────────────────────────────────────────────────────────────
const modalOpen        = ref(false)
const modalRef         = ref(null)
const showDeletePrompt = ref(false)
const assessmentToDelete = ref(null)

const openAdd = () => {
    modalOpen.value = true
}

const openEdit = (assessment) => {
    modalRef.value?.open(assessment)
}

const confirmDelete = (assessment) => {
    assessmentToDelete.value = assessment
    showDeletePrompt.value   = true
}

const handleDelete = () => {
    router.delete(`/staff/manage-assessments/${assessmentToDelete.value.id}`, {
        onSuccess: (page) => {
            showDeletePrompt.value   = false
            assessmentToDelete.value = null
            flashToast(page.props.flash)
        },
        onError: () => flashToast({ type: 'error', message: 'Failed to delete assessment' }),
    })
}
</script>

<template>
    <Layout>
        <Head title="Assessments" />

        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Assessments</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage CA and exam definitions per term</p>
            </div>
            <button @click="openAdd"
                class="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition">
                <PhNotePencil :size="18" /> Create Assessment
            </button>
        </div>

        <Table
            :data="{ data: assessments, links: [] }"
            :columns="columns"
            variant="staff"
            row-key="id"
            :expanded-id="expandedId"
            :actions="{ view: false, edit: false, delete: false }"
            :filters="[
                {
                    key:     'session',
                    label:   'Session',
                    options: [{ label: 'All Sessions', value: null }, ...sessions.map(s => ({ label: s.name, value: s.id }))],
                    value:   filters.session,
                },
                {
                    key:     'term',
                    label:   'Term',
                    options: termFilterOptions,
                    value:   filters.term,
                },
                {
                    key:     'type',
                    label:   'Type',
                    options: [{ label: 'All Types', value: null }, ...types.map(t => ({ label: `${t.name} (${t.code})`, value: t.id }))],
                    value:   filters.type,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
        >
            <template #name="{ row }">
                <button class="flex items-center gap-1.5 font-semibold text-primary hover:text-primary-light transition"
                    @click="toggleExpand(row.id)">
                    <PhCaretRight :size="14" class="transition-transform"
                        :class="expandedId === row.id ? 'rotate-90' : ''" />
                    {{ row.name }}
                </button>
            </template>

            <template #session="{ row }">
                <span class="text-sm text-gray-700">{{ row.academic_session?.name ?? '—' }}</span>
            </template>

            <template #term="{ row }">
                <span class="text-sm text-gray-700">{{ row.academic_term?.name ?? '—' }}</span>
            </template>

            <template #type="{ row }">
                <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md border"
                    :class="row.type?.code === 'CA'
                        ? 'bg-purple-50 text-purple-600 border-purple-100'
                        : 'bg-indigo-50 text-indigo-600 border-indigo-100'">
                    {{ row.type?.code ?? '—' }}
                </span>
            </template>

            <template #weight="{ row }">
                <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                             bg-gray-50 text-gray-600 rounded-md border border-gray-200">
                    {{ row.weight }}%
                </span>
            </template>

            <template #classes="{ row }">
                <button v-if="row.classes.length"
                    class="flex items-center gap-1.5 text-sm text-gray-600 hover:text-primary transition"
                    @click="toggleExpand(row.id)">
                    <PhBank :size="14" class="text-gray-400" />
                    <span>{{ row.classes.length }} class{{ row.classes.length !== 1 ? 'es' : '' }}</span>
                    <span class="text-gray-300">·</span>
                    <PhBook :size="14" class="text-gray-400" />
                    <span>{{ getTotalSubjects(row.classes) }} subject{{ getTotalSubjects(row.classes) !== 1 ? 's' : '' }}</span>
                </button>
                <span v-else class="text-xs text-gray-400 italic">No classes</span>
            </template>

            <template #status="{ row }">
                <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full border"
                    :class="row.is_active
                        ? 'bg-green-50 text-green-700 border-green-100'
                        : 'bg-gray-50 text-gray-500 border-gray-200'">
                    <span class="w-1.5 h-1.5 rounded-full"
                        :class="row.is_active ? 'bg-green-500' : 'bg-gray-400'"></span>
                    {{ row.is_active ? 'Active' : 'Inactive' }}
                </span>
            </template>

            <template #actions="{ row }">
                <div class="flex items-center justify-end gap-2">
                    <button @click="openEdit(row)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-green-600 bg-green-50 hover:bg-green-100 rounded-md border border-green-100 transition">
                        <PhPencilSimple :size="13" /> Edit
                    </button>
                    <button @click="confirmDelete(row)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-red-600 bg-red-50 hover:bg-red-100 rounded-md border border-red-100 transition">
                        <PhTrash :size="13" /> Delete
                    </button>
                </div>
            </template>

            <template #expanded="{ row }">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div v-for="cls in row.classes" :key="cls.id"
                        class="bg-white border border-gray-200 rounded-lg p-3">
                        <div class="flex items-center gap-2 mb-2">
                            <PhBank :size="14" class="text-primary shrink-0" />
                            <span class="text-sm font-semibold text-gray-800">{{ cls.name }}</span>
                        </div>
                        <div v-if="cls.subjects?.length" class="flex flex-wrap gap-1.5">
                            <span v-for="subject in cls.subjects" :key="subject.id"
                                class="inline-flex items-center gap-1 px-2 py-0.5 text-xs
                                       bg-primary-50 text-primary rounded-md border border-primary-100">
                                {{ subject.name }}
                            </span>
                        </div>
                        <p v-else class="text-xs text-gray-400 italic">No subjects assigned</p>
                    </div>
                </div>
            </template>
        </Table>

        <AssessmentModal
            ref="modalRef"
            v-model="modalOpen"
            :sessions="sessions"
            :terms="terms"
            :classes="classes"
            :session-terms="sessionTerms"
            :types="types"
            :assessments="assessments"
        />

        <Prompt
            :isShow="showDeletePrompt"
            title="Delete Assessment"
            :message="`Are you sure you want to delete '${assessmentToDelete?.name}'? This cannot be undone.`"
            confirmText="Delete"
            @delete="handleDelete"
            @cancel="() => { showDeletePrompt = false; assessmentToDelete = null }"
        />
    </Layout>
</template>